import React, { useState, useRef, useEffect, useCallback } from 'react';
import Hls from 'hls.js';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5002';

function LiveStream({ mode }) {
  const [hlsUrl, setHlsUrl] = useState('');
  const [streamId, setStreamId] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [latestResult, setLatestResult] = useState(null);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [frameCount, setFrameCount] = useState(0);
  const [error, setError] = useState(null);
  const [complianceHistory, setComplianceHistory] = useState([]);
  const [videoError, setVideoError] = useState(false);

  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const eventSourceRef = useRef(null);
  const activeHlsUrl = useRef(null);

  // Initialize HLS player after the video element is in the DOM
  useEffect(() => {
    if (!isStreaming || !activeHlsUrl.current) return;

    const video = videoRef.current;
    if (!video) return;

    // Cleanup previous instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    setVideoError(false);
    const url = activeHlsUrl.current;

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
      });
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(() => {});
      });
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          console.error('HLS player error:', data.type, data.details);
          setVideoError(true);
          hls.destroy();
        }
      });
      hlsRef.current = hls;
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS
      video.src = url;
      video.addEventListener('loadedmetadata', () => {
        video.play().catch(() => {});
      });
      video.addEventListener('error', () => {
        setVideoError(true);
      });
    } else {
      setVideoError(true);
    }
  }, [isStreaming]);

  // Start stream analysis
  const handleStart = async () => {
    if (!hlsUrl.trim()) {
      setError('Please enter an HLS stream URL');
      return;
    }

    setError(null);
    setIsConnecting(true);
    setLatestResult(null);
    setRecentAlerts([]);
    setFrameCount(0);
    setComplianceHistory([]);

    try {
      const res = await fetch(`${API_BASE}/api/stream/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hlsUrl: hlsUrl.trim(), mode, interval: 3 })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start stream');

      setStreamId(data.streamId);
      setIsConnecting(false);

      // Store URL for the useEffect that initializes the player after render
      activeHlsUrl.current = hlsUrl.trim();
      setIsStreaming(true);

      // Connect to SSE for real-time results
      const es = new EventSource(`${API_BASE}/api/stream/${data.streamId}/events`);

      es.onmessage = (event) => {
        const result = JSON.parse(event.data);
        setLatestResult(result);
        setFrameCount(result.frameNumber);

        // Add to compliance history (keep last 20)
        setComplianceHistory(prev => {
          const next = [...prev, { frame: result.frameNumber, score: result.complianceScore }];
          return next.slice(-20);
        });

        // Merge new alerts (keep last 10)
        if (result.alerts && result.alerts.length > 0) {
          setRecentAlerts(prev => {
            const newAlerts = result.alerts.map(a => ({
              ...a,
              frameNumber: result.frameNumber,
              timestamp: result.timestamp
            }));
            return [...newAlerts, ...prev].slice(0, 10);
          });
        }
      };

      es.addEventListener('error', () => {
        // SSE reconnects automatically; only show error if stream was supposed to be running
      });

      es.addEventListener('stopped', () => {
        setIsStreaming(false);
        es.close();
      });

      eventSourceRef.current = es;
    } catch (err) {
      setError(err.message);
      setIsConnecting(false);
      setIsStreaming(false);
    }
  };

  // Stop stream
  const handleStop = async () => {
    if (streamId) {
      try {
        await fetch(`${API_BASE}/api/stream/${streamId}/stop`, { method: 'POST' });
      } catch (e) { /* ignore */ }
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    activeHlsUrl.current = null;
    setVideoError(false);
    setIsStreaming(false);
    setStreamId(null);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) eventSourceRef.current.close();
      if (hlsRef.current) hlsRef.current.destroy();
    };
  }, []);

  const severityIcon = (severity) => {
    if (severity === 'critical') return (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="#ef4444">
        <path d="M8 1L1 14h14L8 1zm0 4.5l1 5H7l1-5zm0 7a1 1 0 110-2 1 1 0 010 2z"/>
      </svg>
    );
    if (severity === 'warning') return (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="#f59e0b">
        <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 10a1 1 0 110-2 1 1 0 010 2zm1-4H7V4h2v3z"/>
      </svg>
    );
    return (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="#3b82f6">
        <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 10.5a.75.75 0 110-1.5.75.75 0 010 1.5zM8.75 4.5v4a.75.75 0 01-1.5 0v-4a.75.75 0 011.5 0z"/>
      </svg>
    );
  };

  return (
    <div className="live-stream-container">
      {/* URL Input Section */}
      <div className="stream-input-section card">
        <h3 className="card-title">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor">
            <circle cx="9" cy="9" r="3" fill="#ef4444"/>
            <circle cx="9" cy="9" r="6" fill="none" stroke="#ef4444" strokeWidth="1.5"/>
            <circle cx="9" cy="9" r="8.5" fill="none" stroke="#ef4444" strokeWidth="1" strokeDasharray="3 2"/>
          </svg>
          Live Stream Analysis
        </h3>
        <p className="stream-desc">
          Enter an HLS (.m3u8) stream URL to perform real-time safety detection.
          The AI will analyze frames at regular intervals.
        </p>

        <div className="stream-input-row">
          <div className="stream-url-field">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="input-icon">
              <path d="M6.354 5.5H4a3 3 0 000 6h3a3 3 0 002.83-4H8.83A2 2 0 017 9.5H4a2 2 0 110-4h2.354a4.01 4.01 0 010 0zM9 4.5a3 3 0 013 3h-1a2 2 0 00-2-2V4.5zm1.646 5H12a3 3 0 000-6H9a3 3 0 00-2.83 4h1.17A2 2 0 019 5.5h3a2 2 0 110 4h-2.354z"/>
            </svg>
            <input
              type="text"
              value={hlsUrl}
              onChange={(e) => setHlsUrl(e.target.value)}
              placeholder="https://example.com/stream/playlist.m3u8"
              disabled={isStreaming || isConnecting}
              className="stream-url-input"
            />
          </div>

          {!isStreaming ? (
            <button
              onClick={handleStart}
              disabled={isConnecting}
              className="stream-btn stream-btn-start"
            >
              {isConnecting ? (
                <>
                  <div className="spinner-small"></div>
                  Connecting...
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M4 2l10 6-10 6V2z"/>
                  </svg>
                  Start Analysis
                </>
              )}
            </button>
          ) : (
            <button onClick={handleStop} className="stream-btn stream-btn-stop">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <rect x="3" y="3" width="10" height="10" rx="1"/>
              </svg>
              Stop
            </button>
          )}
        </div>

        {error && (
          <div className="upload-error" style={{ marginTop: 12 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 10.5a.75.75 0 110-1.5.75.75 0 010 1.5zM8.75 4.5v4a.75.75 0 01-1.5 0v-4a.75.75 0 011.5 0z"/>
            </svg>
            {error}
          </div>
        )}
      </div>

      {/* Live Content */}
      {(isStreaming || latestResult) && (
        <div className="stream-live-grid">
          {/* Left: Annotated Frame (primary) + Video Player */}
          <div className="stream-left">
            {/* Annotated Detection Frame — Primary View */}
            {latestResult ? (
              <div className="stream-detection-card card">
                <h4 className="card-title">
                  <span className="live-badge">
                    <span className="live-dot"></span>
                    LIVE
                  </span>
                  AI Detection — Frame #{latestResult.frameNumber}
                  <span className="processing-chip">{latestResult.processingTimeMs}ms</span>
                </h4>
                <div className="stream-annotated-img">
                  <img
                    src={`${API_BASE}${latestResult.annotatedImage}`}
                    alt={`Detection frame ${latestResult.frameNumber}`}
                  />
                </div>
              </div>
            ) : (
              <div className="stream-detection-card card">
                <h4 className="card-title">
                  <span className="live-badge">
                    <span className="live-dot"></span>
                    LIVE
                  </span>
                  AI Detection
                </h4>
                <div className="stream-waiting-frame">
                  <div className="spinner"></div>
                  <p>Capturing first frame from stream...</p>
                </div>
              </div>
            )}

            {/* Raw Stream Player (secondary) */}
            {!videoError ? (
              <div className="stream-video-card card">
                <h4 className="card-title">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M2.5 4A1.5 1.5 0 014 2.5h8A1.5 1.5 0 0113.5 4v8a1.5 1.5 0 01-1.5 1.5H4A1.5 1.5 0 012.5 12V4zM6 5.5v5l4.5-2.5L6 5.5z"/>
                  </svg>
                  Raw Stream Feed
                </h4>
                <div className="stream-video-wrapper">
                  <video
                    ref={videoRef}
                    muted
                    autoPlay
                    playsInline
                    className="stream-video"
                  />
                </div>
              </div>
            ) : (
              <div className="stream-video-card card">
                <h4 className="card-title">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M2.5 4A1.5 1.5 0 014 2.5h8A1.5 1.5 0 0113.5 4v8a1.5 1.5 0 01-1.5 1.5H4A1.5 1.5 0 012.5 12V4zM6 5.5v5l4.5-2.5L6 5.5z"/>
                  </svg>
                  Raw Stream Feed
                </h4>
                <div className="stream-video-fallback">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="4" y="8" width="24" height="16" rx="2"/>
                    <path d="M12 13v6l6-3-6-3z"/>
                    <line x1="4" y1="4" x2="28" y2="28" strokeWidth="2" stroke="#ef4444"/>
                  </svg>
                  <p>Browser can't play this stream directly (CORS)</p>
                  <span>AI detection frames above are captured server-side and work normally</span>
                </div>
              </div>
            )}
          </div>

          {/* Right: Metrics + Alerts */}
          <div className="stream-right">
            {/* Live Compliance Score */}
            <div className="stream-score-card card">
              <h4 className="card-title">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 1L2 4v4c0 3.5 2.56 6.77 6 7.5 3.44-.73 6-4 6-7.5V4L8 1z"/>
                </svg>
                Live Compliance
              </h4>
              {latestResult ? (
                <div className="stream-score-display">
                  <div className={`stream-score-value ${
                    latestResult.complianceScore >= 80 ? 'score-good' :
                    latestResult.complianceScore >= 50 ? 'score-warning' : 'score-critical'
                  }`}>
                    {latestResult.complianceScore}%
                  </div>
                  <div className="stream-score-meta">
                    <div className="stream-stat">
                      <span className="stream-stat-val">{latestResult.summary?.totalPersons || 0}</span>
                      <span className="stream-stat-label">Persons</span>
                    </div>
                    <div className="stream-stat">
                      <span className="stream-stat-val">{latestResult.summary?.helmetsDetected || 0}</span>
                      <span className="stream-stat-label">Helmets</span>
                    </div>
                    <div className="stream-stat">
                      <span className="stream-stat-val">{latestResult.summary?.vestsDetected || 0}</span>
                      <span className="stream-stat-label">Vests</span>
                    </div>
                    <div className="stream-stat">
                      <span className="stream-stat-val">{latestResult.summary?.violations || 0}</span>
                      <span className="stream-stat-label">Violations</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="stream-score-waiting">
                  <div className="spinner-small"></div>
                  Waiting for first frame...
                </div>
              )}
            </div>

            {/* Compliance Timeline */}
            {complianceHistory.length > 1 && (
              <div className="stream-timeline-card card">
                <h4 className="card-title">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M1 14h14V2h-1v11H1v1zm1-2h1v-4H2v4zm3 0h1V5H5v7zm3 0h1V8H8v4zm3 0h1V3h-1v9z"/>
                  </svg>
                  Compliance Timeline
                </h4>
                <div className="stream-chart">
                  {complianceHistory.map((item, i) => (
                    <div
                      key={i}
                      className="stream-chart-bar"
                      style={{ height: `${item.score}%` }}
                      title={`Frame ${item.frame}: ${item.score}%`}
                    >
                      <div className={`stream-chart-fill ${
                        item.score >= 80 ? 'fill-good' :
                        item.score >= 50 ? 'fill-warning' : 'fill-critical'
                      }`} />
                    </div>
                  ))}
                </div>
                <div className="stream-chart-labels">
                  <span>Frame {complianceHistory[0]?.frame}</span>
                  <span>Frame {complianceHistory[complianceHistory.length - 1]?.frame}</span>
                </div>
              </div>
            )}

            {/* Live Alerts */}
            <div className="stream-alerts-card card">
              <h4 className="card-title">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 1.5L1.5 13h13L8 1.5zm0 3.5l1 4H7l1-4zm0 6a.75.75 0 110-1.5.75.75 0 010 1.5z"/>
                </svg>
                Live Alerts
                {recentAlerts.length > 0 && (
                  <span className="alert-count-badge">{recentAlerts.length}</span>
                )}
              </h4>
              <div className="stream-alerts-list">
                {recentAlerts.length === 0 ? (
                  <div className="stream-no-alerts">
                    {isStreaming ? 'Monitoring for violations...' : 'No alerts recorded'}
                  </div>
                ) : (
                  recentAlerts.map((alert, i) => (
                    <div key={i} className={`stream-alert-item severity-${alert.severity}`}>
                      <div className="stream-alert-icon">
                        {severityIcon(alert.severity)}
                      </div>
                      <div className="stream-alert-content">
                        <span className="stream-alert-msg">{alert.message}</span>
                        <span className="stream-alert-meta">
                          Frame #{alert.frameNumber} &middot; {new Date(alert.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Stream Info */}
            <div className="stream-info-card card">
              <div className="stream-info-row">
                <span>Frames Analyzed</span>
                <span className="stream-info-val">{frameCount}</span>
              </div>
              <div className="stream-info-row">
                <span>Analysis Interval</span>
                <span className="stream-info-val">3s</span>
              </div>
              <div className="stream-info-row">
                <span>AI Model</span>
                <span className="stream-info-val">COCO-SSD v2</span>
              </div>
              <div className="stream-info-row">
                <span>Status</span>
                <span className={`stream-info-val ${isStreaming ? 'text-green' : 'text-dim'}`}>
                  {isStreaming ? 'Analyzing' : 'Stopped'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LiveStream;
